import { motion } from "framer-motion";
import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Mic, Award, Newspaper, Briefcase, BookOpen, Mail, Linkedin } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const inquiryTypes = [
  { icon: Mic, label: "Speaking", value: "speaking", desc: "Keynotes, panels, executive briefings" },
  { icon: Award, label: "Board & Advisory", value: "board", desc: "Corporate, nonprofit, advisory councils" },
  { icon: Newspaper, label: "Media", value: "media", desc: "Interviews, press, podcast appearances" },
  { icon: Briefcase, label: "Partnerships", value: "partnerships", desc: "Strategic collaborations" },
  { icon: BookOpen, label: "Executive Briefings", value: "briefing", desc: "Private sessions for leadership teams" },
  { icon: Mail, label: "General Contact", value: "general", desc: "Other inquiries" },
];

const ContactPage = () => {
  const [selectedType, setSelectedType] = useState("");
  const [formData, setFormData] = useState({ name: "", email: "", organization: "", message: "" });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/speaking/inquiry`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...formData, eventType: selectedType }),
      });
      if (res.ok) {
        toast.success("Thank you! I'll be in touch soon.");
        setFormData({ name: "", email: "", organization: "", message: "" });
        setSelectedType("");
      } else {
        toast.error("Something went wrong.");
      }
    } catch {
      toast.error("Connection error.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="pt-24 pb-20 min-h-screen">
      <div className="max-w-4xl mx-auto px-6 md:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-12">
          <p className="text-sm uppercase tracking-[0.25em] text-[var(--accent-violet-light)] font-medium mb-4">Contact</p>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white italic mb-4" data-testid="contact-heading">
            Let's Connect
          </h1>
          <p className="text-[var(--text-secondary)] text-lg">Select the type of inquiry that best describes your interest.</p>
          <a href="https://www.linkedin.com/in/raulvin-coke-705189392/" target="_blank" rel="noopener noreferrer" className="mt-5 inline-flex items-center gap-2 text-sm text-white border border-[rgba(124,58,237,0.4)] hover:border-[var(--accent-violet)] px-5 py-2.5 rounded-full transition-colors" data-testid="contact-linkedin-btn">
            <Linkedin className="w-4 h-4" /> Connect with me on LinkedIn
          </a>
        </motion.div>

        {/* Inquiry Type Selection */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
          {inquiryTypes.map((type) => (
            <button
              key={type.value}
              onClick={() => setSelectedType(type.value)}
              className={`text-left p-5 rounded-xl border transition-all ${
                selectedType === type.value
                  ? "bg-[var(--accent-violet)]/15 border-[var(--accent-violet)] shadow-lg shadow-violet-900/20"
                  : "bg-[var(--bg-card)] border-glow hover:border-[rgba(124,58,237,0.3)]"
              }`}
              data-testid={`contact-type-${type.value}`}
            >
              <type.icon className={`w-6 h-6 mb-3 ${selectedType === type.value ? "text-[var(--accent-violet-light)]" : "text-[var(--text-secondary)]"}`} />
              <h3 className="text-white font-semibold mb-1">{type.label}</h3>
              <p className="text-xs text-[var(--text-secondary)]">{type.desc}</p>
            </button>
          ))}
        </div>

        {/* Form */}
        <div className="bg-[var(--bg-card)] border-glow rounded-2xl p-8 md:p-10">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <input type="text" placeholder="Your Name *" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required className="w-full px-4 py-3 bg-[var(--bg-secondary)] border border-[rgba(124,58,237,0.15)] rounded-lg text-white placeholder:text-[var(--text-secondary)] focus:border-[var(--accent-violet)] focus:outline-none" data-testid="contact-name" />
              <input type="email" placeholder="Email *" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required className="w-full px-4 py-3 bg-[var(--bg-secondary)] border border-[rgba(124,58,237,0.15)] rounded-lg text-white placeholder:text-[var(--text-secondary)] focus:border-[var(--accent-violet)] focus:outline-none" data-testid="contact-email" />
            </div>
            <input type="text" placeholder="Organization" value={formData.organization} onChange={(e) => setFormData({ ...formData, organization: e.target.value })} className="w-full px-4 py-3 bg-[var(--bg-secondary)] border border-[rgba(124,58,237,0.15)] rounded-lg text-white placeholder:text-[var(--text-secondary)] focus:border-[var(--accent-violet)] focus:outline-none" />
            <textarea placeholder="How can Raulvin help?" value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} rows={5} className="w-full px-4 py-3 bg-[var(--bg-secondary)] border border-[rgba(124,58,237,0.15)] rounded-lg text-white placeholder:text-[var(--text-secondary)] focus:border-[var(--accent-violet)] focus:outline-none resize-none" />
            <button type="submit" disabled={loading} className="w-full bg-[var(--accent-violet)] hover:bg-[#6d28d9] text-white py-4 rounded-lg font-semibold text-lg transition-all flex items-center justify-center gap-2 disabled:opacity-60" data-testid="contact-submit-btn">
              {loading ? "Sending..." : "Send Inquiry"}
              {!loading && <ArrowRight className="w-5 h-5" />}
            </button>
          </form>
        </div>
      </div>
    </main>
  );
};

export default ContactPage;
